import { RadioGroup } from '@headlessui/react';

import { cn } from '~/styling';

import type { ThemeId } from '~/local/custom-map-puzzle';
import { THEMES } from '~/local/custom-map-puzzle';

// Theme color previews for visual selection
const THEME_COLORS: Record<ThemeId, { bg: string; roads: string }> = {
  noir: { bg: '#1a1a1a', roads: '#ffffff' },
  terracotta: { bg: '#F5EDE4', roads: '#A0522D' },
  midnight_blue: { bg: '#1a2744', roads: '#4a90d9' },
  autumn: { bg: '#f5e6d3', roads: '#c95c34' },
  blueprint: { bg: '#1e3a5f', roads: '#ffffff' },
  contrast_zones: { bg: '#2d2d2d', roads: '#00ffcc' },
  copper_patina: { bg: '#2d4a3e', roads: '#c49c6b' },
  emerald: { bg: '#1a3d2e', roads: '#50c878' },
  forest: { bg: '#2d3b2d', roads: '#90a955' },
  gradient_roads: { bg: '#1a1a2e', roads: '#ff6b6b' },
  japanese_ink: { bg: '#f5f5f0', roads: '#2d2d2d' },
  neon_cyberpunk: { bg: '#0d0221', roads: '#ff00ff' },
  ocean: { bg: '#1a3d5c', roads: '#87ceeb' },
  pastel_dream: { bg: '#fef6e4', roads: '#f582ae' },
  sunset: { bg: '#2d1b4e', roads: '#ff7b54' },
  warm_beige: { bg: '#f5f0e6', roads: '#a0826d' },
};

interface ThemeSelectorProps {
  selected: ThemeId;
  onSelect: (theme: ThemeId) => void;
}

export function ThemeSelector({ selected, onSelect }: ThemeSelectorProps) {
  return (
    <div className="space-y-3">
      <label className="text-primary-2-80 block text-sm font-medium">Color Theme</label>
      <RadioGroup value={selected} onChange={onSelect} className="grid grid-cols-4 gap-2 sm:grid-cols-8">
        {THEMES.map((theme) => {
          const colors = THEME_COLORS[theme.id];

          return (
            <RadioGroup.Option key={theme.id} value={theme.id}>
              {({ checked }) => (
                <div
                  className={cn(
                    'cursor-pointer rounded-lg p-1 transition-all',
                    checked ? 'ring-secondary-1 ring-2 ring-offset-2' : 'hover:ring-1 hover:ring-neutral-2-40'
                  )}
                  title={`${theme.name}: ${theme.description}`}
                >
                  <div
                    className="aspect-square rounded-md"
                    style={{ backgroundColor: colors.bg }}
                  >
                    <svg viewBox="0 0 40 40" className="h-full w-full p-1">
                      <path
                        d="M8,20 Q20,8 32,20"
                        stroke={colors.roads}
                        strokeWidth="2"
                        fill="none"
                      />
                      <path
                        d="M5,30 Q20,15 35,25"
                        stroke={colors.roads}
                        strokeWidth="1.5"
                        fill="none"
                        opacity="0.7"
                      />
                      <path
                        d="M15,5 L15,35"
                        stroke={colors.roads}
                        strokeWidth="1"
                        fill="none"
                        opacity="0.5"
                      />
                    </svg>
                  </div>
                </div>
              )}
            </RadioGroup.Option>
          );
        })}
      </RadioGroup>
      <p className="text-neutral-2-60 text-xs">
        Selected: <span className="font-medium">{THEMES.find((t) => t.id === selected)?.name}</span>
        {' - '}
        {THEMES.find((t) => t.id === selected)?.description}
      </p>
    </div>
  );
}
